/**
 * A mesa do caso aberto por CCB (E177.F6, GUS-253, ADR-0130; HTML 6.8 por CCB
 * `?tela=interno-envio-ccb`):
 *
 * - **Cabeçalho**: "CCB 4201 · 3 documentos · 1 compartilhado" e a nota de que só os documentos
 *   vinculados a esta CCB entram no exame — os das outras CCBs da mesma pasta ficam nos casos delas.
 * - **Compartilhado**: o selo no documento que outro caso da mesma pasta tem pelos mesmos bytes, com a
 *   CCB dele.
 * - **Pendência**: a divergência que quem enviou manteve na abertura, com o número e o lido.
 */
import { textoDasContagens } from '@/components/Abertura/conferenciaPorCcb';
import { Badge } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { CcbDaMesaData, DivergenciaMantidaData } from '@/lib/exame_eletronico';

const t = ptBR.abertura.conferirPorCcb.mesa;
const rotulo = (numero: string): string =>
  ptBR.abertura.contratosDigitais.rotulo.replace('{numero}', numero);

export function CcbDoCasoNaMesa({
  ccb,
}: {
  ccb: CcbDaMesaData | null | undefined;
}): JSX.Element | null {
  if (ccb == null) return null;
  return (
    <div className="mt-1" data-testid="pecas-ccb-do-caso">
      <p className="text-body-sm font-semibold text-fg">
        {t.cabecalho.replace('{ccb}', ccb.numero).replace('{contagens}', textoDasContagens(ccb))}
      </p>
      <p className="mt-1 max-w-prose text-body-sm text-fg-muted">{t.nota}</p>
    </div>
  );
}

export function DivergenciaMantidaNaMesa({
  divergencia,
}: {
  divergencia: DivergenciaMantidaData | null | undefined;
}): JSX.Element | null {
  if (divergencia == null) return null;
  return (
    <div
      className="border border-warning bg-warning-50 px-sc-3 py-sc-2 text-body-sm text-warning-700"
      role="status"
      data-testid="pecas-divergencia-mantida"
    >
      <p className="font-semibold">{t.divergenciaTitulo.replace('{numero}', divergencia.numero)}</p>
      <p>
        {t.divergenciaTexto
          .replace('{fonte}', ptBR.abertura.conferirEnvio.fonte[divergencia.fonte])
          .replace('{lido}', divergencia.lida)}
      </p>
    </div>
  );
}

export function CompartilhadoBadge({
  docId,
  ccbs,
}: {
  docId: string;
  ccbs: readonly string[] | undefined;
}): JSX.Element | null {
  if (ccbs === undefined || ccbs.length === 0) return null;
  const com = t.compartilhadoCom.replace('{ccbs}', ccbs.map(rotulo).join(', '));
  return (
    <Badge
      variant="neutral"
      data-testid={`peca-compartilhada-${docId}`}
      title={com}
      aria-label={com}
    >
      {t.compartilhado}
    </Badge>
  );
}
